import { Box, CircularProgress, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

import { formatOfficeTime } from './syncStatusDisplay';

/** MM-1198: how often (seconds) the Sync Calendar and Sync Jobs pages reload their list by themselves. */
export const AUTO_REFRESH_SECONDS = 10;

/**
 * MM-1198: reloads a page's list every AUTO_REFRESH_SECONDS while the page is open, counting down one second at a
 * time. Only reads the list - nothing is triggered. The caller's load skips a tick while a load is still running.
 * MM-1198: while enabled is false (e.g. the Sync Jobs section is collapsed) nothing is reloaded and the countdown
 * rests at full; it starts over from AUTO_REFRESH_SECONDS once enabled again.
 * load - the page's list load
 * enabled - whether the list is reloaded at all (default true)
 * returns the seconds left until the next reload
 */
export const useAutoRefresh = (load: () => void, enabled = true): number => {
  const [secondsLeft, setSecondsLeft] = useState<number>(AUTO_REFRESH_SECONDS);
  useEffect(() => {
    setSecondsLeft(AUTO_REFRESH_SECONDS);
    if (!enabled) {
      return undefined;
    }
    let seconds = AUTO_REFRESH_SECONDS;
    const timer = window.setInterval(() => {
      seconds -= 1;
      if (seconds <= 0) {
        seconds = AUTO_REFRESH_SECONDS;
        load();
      }
      setSecondsLeft(seconds);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [load, enabled]);
  return secondsLeft;
};

/**
 * MM-1198: "Updated hh:mm:ss" and a circular countdown to the next auto refresh - the ring empties over the
 * seconds left.
 * loadedAt - when the list was last loaded (epoch ms), null before the first load
 * secondsLeft - seconds until the next reload
 */
export const AutoRefreshCountdown = ({ loadedAt, secondsLeft }: { loadedAt: number | null; secondsLeft: number }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    {loadedAt ? (
      <Typography variant='caption' color='text.secondary'>
        Updated {formatOfficeTime(loadedAt, 'hh:mm:ss A')}
      </Typography>
    ) : null}
    <Box
      sx={{ position: 'relative', display: 'inline-flex' }}
      title={`Refreshes automatically - next refresh in ${secondsLeft}s`}
      aria-label={`Next refresh in ${secondsLeft} seconds`}
    >
      <CircularProgress
        variant='determinate'
        value={100}
        size={28}
        thickness={4}
        sx={{ color: '#EAECF0', position: 'absolute' }}
      />
      <CircularProgress
        variant='determinate'
        value={(secondsLeft / AUTO_REFRESH_SECONDS) * 100}
        size={28}
        thickness={4}
      />
      <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant='caption' fontWeight={600} color='text.secondary'>
          {secondsLeft}
        </Typography>
      </Box>
    </Box>
  </Box>
);
